import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { useSeo } from "@/hooks/useSeo";
import { Fuel, CheckCircle2, Upload, ShieldCheck, ChevronDown } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1652509573480-a190f1c61f6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxuZXclMjBkcml2ZXIlMjBjYXIlMjBrZXlzfGVufDB8fHx8MTc5MDQ5MTA4NHww&ixlib=rb-4.1.0&q=85";

const CONDITIONS = [
  "Avoir trouvé son auto-école via Plan Permis (permis accéléré, transfert de dossier ou examen en campagne).",
  "Avoir réussi l'examen du permis de conduire.",
  "Fournir une preuve de réussite (photo du permis ou relevé de notes).",
  "Ne jamais avoir reçu de bon carburant Plan Permis auparavant (une seule fois par personne).",
];

const STEPS = [
  { n: "01", title: "Réussissez votre permis", text: "Vous avez trouvé votre auto-école via Plan Permis et décroché votre permis." },
  { n: "02", title: "Envoyez votre demande", text: "Remplissez le formulaire ci-dessous et joignez une preuve (permis ou relevé de notes)." },
  { n: "03", title: "Nous vérifions", text: "Notre équipe confirme votre réussite auprès de votre auto-école (7 à 15 jours ouvrables)." },
  { n: "04", title: "Vous recevez 25 €", text: "Votre bon carburant vous est envoyé par email. Bonne route !" },
];

const FAQ = [
  {
    q: "Qui peut recevoir le bon carburant de 25 € ?",
    a: "Tout élève ayant trouvé son auto-école via Plan Permis et ayant réussi son permis de conduire, sur présentation d'une preuve de réussite.",
  },
  {
    q: "Combien de fois puis-je recevoir ce bon ?",
    a: "Une seule fois par personne, à vie. L'identification se fait par email, téléphone et par vos nom, prénom et date de naissance.",
  },
  {
    q: "J'ai échoué une première fois puis réussi : ai-je droit au bon ?",
    a: "Oui, si vous n'avez jamais reçu de bon auparavant. Un échec puis une réussite ne donnent pas droit à deux bons.",
  },
  {
    q: "Quel est le délai de traitement ?",
    a: "En moyenne 7 à 15 jours ouvrables à compter de la réception de votre demande et de la confirmation par l'auto-école.",
  },
  {
    q: "Le bon carburant est-il cumulable avec le programme ambassadeurs ?",
    a: "Oui. Le bon carburant (pour l'élève qui réussit) est cumulable avec la commission de recommandation (pour le parrain).",
  },
];

export default function BonCarburant() {
  useSeo({
    title: "Bon carburant 25 € pour jeunes conducteurs | Plan Permis",
    description:
      "Vous avez réussi votre permis via Plan Permis ? Recevez un bon carburant de 25 €, une seule fois par personne. Conditions et demande en ligne.",
    path: "/bon-carburant",
    image: HERO_IMG,
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Offer",
        name: "Bon carburant 25 € — Plan Permis",
        description:
          "Bon carburant de 25 € offert à tout élève ayant réussi son permis de conduire via Plan Permis, une seule fois par personne.",
        price: "25",
        priceCurrency: "EUR",
        category: "Récompense élève",
        seller: { "@type": "Organization", name: "Plan Permis" },
      },
      {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: "Comment recevoir son bon carburant de 25 €",
        step: STEPS.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.title,
          text: s.text,
        })),
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  });

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    date_naissance: "",
    auto_ecole: "",
    date_reussite: "",
    forfait_choisi: "",
    accepted_terms: false,
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);

  const inputCls =
    "w-full rounded-lg border border-neutral-300 bg-white px-fib-4 py-fib-3 text-gr-base outline-none focus:border-brand transition-colors";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error("Merci de renseigner nom, prénom, email et téléphone.");
      return;
    }
    if (!form.accepted_terms) {
      toast.error("Vous devez accepter les conditions du programme Bon Carburant.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("preuve", file);
      const { data } = await api.post("/bon-carburant/demande", fd);
      setResult(data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-fib-4 sm:px-12">
        <Link to="/" data-testid="bon-logo-link">
          <Logo className="h-12" />
        </Link>
        <Link
          to="/ambassadeur"
          className="text-gr-sm font-semibold text-neutral-600 transition-colors hover:text-brand"
        >
          Programme ambassadeurs
        </Link>
      </header>

      <main>
        {/* Hero */}
        <section className="bg-gradient-to-br from-brand to-brand-active px-6 py-fib-7 text-white sm:px-12">
          <div className="mx-auto grid max-w-6xl items-center gap-fib-6 lg:grid-cols-2">
            <div>
              <span className="inline-flex items-center gap-fib-2 rounded-full bg-white/15 px-fib-4 py-fib-1 text-gr-xs font-bold uppercase tracking-[0.2em]">
                <Fuel className="h-4 w-4" /> Bon carburant · 25 €
              </span>
              <h1 className="mt-fib-5 text-balance text-[2.618rem] font-black leading-[1.02] tracking-tight sm:text-gr-3xl" data-testid="bon-title">
                Permis réussi&nbsp;? Recevez 25 € de carburant
              </h1>
              <p className="mt-fib-4 measure text-gr-lg text-white/90">
                Notre façon de vous féliciter&nbsp;: un bon carburant de <strong>25 €</strong> pour tout élève qui
                décroche son permis via Plan Permis. <strong>Une seule fois par personne</strong>, sous conditions.
              </p>
              <a
                href="#demande"
                data-testid="hero-cta-btn"
                className="mt-fib-6 inline-block rounded-full bg-white px-fib-6 py-fib-4 text-gr-base font-bold text-brand transition-transform hover:scale-[1.02]"
              >
                Demander mon bon carburant
              </a>
            </div>
            <div className="relative">
              <img
                src={HERO_IMG}
                alt="Jeune conducteur tenant les clés de sa voiture après avoir réussi son permis"
                className="aspect-[4/3] w-full rounded-3xl object-cover shadow-2xl"
                loading="eager"
                width="640"
                height="480"
              />
              <div className="absolute -bottom-fib-3 -left-fib-3 rounded-2xl bg-white px-fib-4 py-fib-3 shadow-xl">
                <p className="text-gr-2xl font-black leading-none text-brand">25 €</p>
                <p className="text-gr-xs font-semibold uppercase tracking-wider text-neutral-500">offerts</p>
              </div>
            </div>
          </div>
        </section>

        {/* Conditions d'éligibilité */}
        <section aria-labelledby="conditions-title" className="px-6 py-fib-7 sm:px-12">
          <div className="mx-auto max-w-3xl">
            <h2 id="conditions-title" className="text-gr-2xl font-extrabold tracking-tight text-neutral-900">
              Conditions d’éligibilité
            </h2>
            <ul className="mt-fib-5 space-y-fib-3">
              {CONDITIONS.map((c, i) => (
                <li key={c} className="flex items-start gap-fib-3 text-gr-base text-neutral-700" data-testid={`bon-cond-${i}`}>
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-brand" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
            <div className="mt-fib-5 flex items-start gap-fib-3 rounded-2xl bg-neutral-100 p-fib-5 text-gr-sm text-neutral-600">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-neutral-500" />
              <p>
                Le traitement prend en moyenne <strong>7 à 15 jours ouvrables</strong> après confirmation de votre
                réussite par l’auto-école. Toute fausse déclaration entraîne l’annulation du bon.
              </p>
            </div>
          </div>
        </section>

        {/* Comment ça marche */}
        <section aria-labelledby="how-title" className="bg-white px-6 py-fib-7 sm:px-12">
          <div className="mx-auto max-w-6xl">
            <h2 id="how-title" className="text-center text-gr-2xl font-extrabold tracking-tight text-neutral-900">
              Comment recevoir votre bon ?
            </h2>
            <ol className="mt-fib-6 grid gap-fib-5 sm:grid-cols-2 lg:grid-cols-4">
              {STEPS.map((s, i) => (
                <li key={s.n} className="rounded-2xl border border-neutral-200 bg-[#FAFAFA] p-fib-5" data-testid={`bon-step-${i + 1}`}>
                  <span className="font-display text-gr-xl font-black text-neutral-200">{s.n}</span>
                  <h3 className="mt-fib-3 text-gr-lg font-bold text-neutral-900">{s.title}</h3>
                  <p className="mt-fib-2 text-gr-sm text-neutral-500">{s.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Formulaire / résultat */}
        <section aria-labelledby="demande-title" id="demande" className="px-6 py-fib-7 sm:px-12">
          <div className="mx-auto max-w-xl">
            <h2 id="demande-title" className="sr-only">Formulaire de demande de bon carburant</h2>
            {result ? (
              <div
                className={`rounded-3xl border p-fib-6 text-center shadow-lg ${
                  result.refused ? "border-red-200 bg-white" : "border-emerald-200 bg-white"
                }`}
                data-testid="bon-result"
              >
                <div
                  className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${
                    result.refused ? "bg-red-100 text-red-600" : "bg-emerald-100 text-emerald-600"
                  }`}
                >
                  {result.refused ? <ShieldCheck className="h-8 w-8" /> : <CheckCircle2 className="h-8 w-8" />}
                </div>
                <h3 className="mt-fib-5 text-gr-xl font-extrabold text-neutral-900">
                  {result.refused ? "Demande non éligible" : "Demande enregistrée !"}
                </h3>
                <p className="mt-fib-2 text-gr-base text-neutral-500">{result.message}</p>
                {!result.refused && (
                  <p className="mt-fib-2 text-gr-sm text-neutral-400">
                    Un email de confirmation vous a été envoyé. Nous revenons vers vous après vérification.
                  </p>
                )}
                <Link
                  to="/"
                  className="mt-fib-6 inline-block rounded-full border border-neutral-300 px-fib-6 py-fib-3 text-gr-sm font-bold text-neutral-800 transition-colors hover:border-brand hover:text-brand"
                >
                  Retour à l’accueil
                </Link>
              </div>
            ) : (
              <form onSubmit={submit} className="rounded-3xl border border-neutral-200 bg-white p-fib-6 shadow-lg" data-testid="bon-form">
                <h3 className="text-gr-xl font-extrabold tracking-tight text-neutral-900">Demander mon bon carburant</h3>
                <p className="mt-fib-2 text-gr-sm text-neutral-500">Réponse sous 7 à 15 jours ouvrables après vérification.</p>
                <div className="mt-fib-5 grid gap-fib-4 sm:grid-cols-2">
                  <input data-testid="bon-firstname" className={inputCls} placeholder="Prénom" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} />
                  <input data-testid="bon-lastname" className={inputCls} placeholder="Nom" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
                </div>
                <input data-testid="bon-email" type="email" className={`${inputCls} mt-fib-4`} placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} />
                <input data-testid="bon-phone" className={`${inputCls} mt-fib-4`} placeholder="Téléphone" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
                <div className="mt-fib-4 grid gap-fib-4 sm:grid-cols-2">
                  <label className="block">
                    <span className="mb-fib-1 block text-gr-xs font-semibold text-neutral-500">Date de naissance</span>
                    <input data-testid="bon-birth" type="date" className={inputCls} value={form.date_naissance} onChange={(e) => set("date_naissance", e.target.value)} />
                  </label>
                  <label className="block">
                    <span className="mb-fib-1 block text-gr-xs font-semibold text-neutral-500">Date de réussite</span>
                    <input data-testid="bon-success-date" type="date" className={inputCls} value={form.date_reussite} onChange={(e) => set("date_reussite", e.target.value)} />
                  </label>
                </div>
                <input data-testid="bon-school" className={`${inputCls} mt-fib-4`} placeholder="Auto-école fréquentée" value={form.auto_ecole} onChange={(e) => set("auto_ecole", e.target.value)} />
                <label className="mt-fib-4 block">
                  <span className="mb-fib-1 block text-gr-xs font-semibold text-neutral-500">Quel forfait avez-vous choisi auprès de votre auto-école ?</span>
                  <input data-testid="bon-forfait" className={inputCls} placeholder="Ex : Permis B accéléré 20 h — 1290 €" value={form.forfait_choisi} onChange={(e) => set("forfait_choisi", e.target.value)} />
                </label>

                <label className="mt-fib-4 flex cursor-pointer items-center gap-fib-3 rounded-lg border border-dashed border-neutral-300 px-fib-4 py-fib-3 text-gr-sm text-neutral-600 hover:border-brand">
                  <Upload className="h-5 w-5 text-neutral-400" />
                  <span className="flex-1">{file ? file.name : "Joindre une preuve (permis ou relevé) — image ou PDF"}</span>
                  <input
                    data-testid="bon-file"
                    type="file"
                    accept="image/*,application/pdf"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </label>

                <label className="mt-fib-4 flex items-start gap-fib-3 text-gr-sm text-neutral-600">
                  <input
                    data-testid="bon-terms"
                    type="checkbox"
                    checked={form.accepted_terms}
                    onChange={(e) => set("accepted_terms", e.target.checked)}
                    className="mt-1 h-4 w-4 accent-brand"
                  />
                  <span>Je certifie l’exactitude des informations et j’accepte les{" "}
                    <Link to="/conditions-recompenses" className="font-semibold text-brand underline">conditions du programme Bon Carburant</Link>.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={loading}
                  data-testid="bon-submit"
                  className="mt-fib-5 w-full rounded-full bg-brand px-fib-6 py-fib-4 text-gr-base font-bold text-white transition-colors hover:bg-brand-hover disabled:opacity-60"
                >
                  {loading ? "Envoi…" : "Envoyer ma demande"}
                </button>
              </form>
            )}
          </div>
        </section>

        {/* FAQ */}
        <section aria-labelledby="faq-title" className="bg-white px-6 py-fib-7 sm:px-12">
          <div className="mx-auto max-w-3xl">
            <h2 id="faq-title" className="text-gr-2xl font-extrabold tracking-tight text-neutral-900">
              Questions fréquentes
            </h2>
            <div className="mt-fib-5 divide-y divide-neutral-200 rounded-2xl border border-neutral-200">
              {FAQ.map((f, i) => (
                <div key={f.q} data-testid={`bon-faq-${i}`}>
                  <button
                    type="button"
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="flex w-full items-center justify-between gap-fib-3 px-fib-5 py-fib-4 text-left"
                    aria-expanded={openFaq === i}
                  >
                    <span className="text-gr-base font-bold text-neutral-900">{f.q}</span>
                    <ChevronDown className={`h-5 w-5 shrink-0 text-neutral-400 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && (
                    <p className="px-fib-5 pb-fib-4 text-gr-sm text-neutral-600">{f.a}</p>
                  )}
                </div>
              ))}
            </div>
            <p className="mt-fib-5 text-center text-gr-sm text-neutral-500">
              Vous recommandez Plan Permis&nbsp;?{" "}
              <Link to="/ambassadeur" className="font-semibold text-brand hover:underline">
                Découvrez le programme ambassadeurs (25 € à 50 € par filleul)
              </Link>
              .
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
